import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Snowflake, LayoutDashboard, Receipt, Users2, FileText, BarChart3, IdCard, LogOut, KeyRound, AlertTriangle, Building2, ScanText } from "lucide-react";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/sales", label: "Sales", icon: Receipt },
  { to: "/admin/customers", label: "Customers", icon: Users2 },
  { to: "/admin/billing", label: "Monthly Bill", icon: FileText },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/verification", label: "Book Verification", icon: ScanText },
  { to: "/admin/settings", label: "Print Settings", icon: Building2 },
  { to: "/admin/users", label: "Labour Users", icon: IdCard },
];

function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const save = async () => {
    if (nw !== confirm) { toast.error("New passwords do not match"); return; }
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed");
      setCur(""); setNw(""); setConfirm(""); onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader><DialogTitle>Change Admin Password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Current Password</Label><Input type="password" data-testid="current-password-input" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
          <div><Label>New Password</Label><Input type="password" data-testid="new-admin-password-input" value={nw} onChange={(e) => setNw(e.target.value)} /></div>
          <div><Label>Confirm New Password</Label><Input type="password" data-testid="confirm-password-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-admin-password-button" className="bg-sky-600 hover:bg-sky-700" onClick={save}>Update Password</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetDataDialog({ open, onOpenChange, onDone }) {
  const [text, setText] = useState("");
  const [finalOpen, setFinalOpen] = useState(false);
  const armed = text === "RESET";

  const doReset = async () => {
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: "RESET" });
      const d = data.deleted;
      toast.success(`All data reset — removed ${d.sales} sales, ${d.customers} customers, ${d.bills} bills`);
      setFinalOpen(false); setText(""); onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) setText(""); onOpenChange(o); }}>
        <DialogContent data-testid="reset-data-dialog">
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Complete Reset — Delete All Data</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-700">
              This will <b>permanently delete ALL application data</b> — every customer, sale, monthly bill and old-balance entry — and <b>cannot be undone</b>. Dashboard totals will return to zero.
            </div>
            <p className="text-slate-600">Your admin account, labour logins, plants (SFX 1 / SFX 2) and all app settings are kept.</p>
            <div>
              <Label>Type <span className="font-mono font-bold">RESET</span> to enable the button</Label>
              <Input data-testid="reset-confirm-input" className="mt-1.5" value={text} onChange={(e) => setText(e.target.value)} placeholder="RESET" autoComplete="off" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setText(""); onOpenChange(false); }}>Cancel</Button>
            <Button data-testid="reset-continue-button" className="bg-red-600 hover:bg-red-700 disabled:opacity-50" disabled={!armed} onClick={() => setFinalOpen(true)}>
              Reset All Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={finalOpen} onOpenChange={setFinalOpen}>
        <AlertDialogContent data-testid="reset-final-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This is your final confirmation. All customers, sales and bills will be permanently erased. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back</AlertDialogCancel>
            <AlertDialogAction data-testid="reset-final-button" className="bg-red-600 hover:bg-red-700" onClick={doReset}>Yes, delete everything</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="no-print w-64 shrink-0 bg-slate-900 text-slate-300 flex-col hidden md:flex fixed inset-y-0">
        <div className="px-5 py-6 flex items-center gap-3 border-b border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800">
          <span className="p-2 rounded-lg bg-sky-500/20 text-sky-400"><Snowflake className="h-6 w-6" /></span>
          <div className="font-chivo font-black text-white leading-tight text-sm">
            SFX ICE PLANT<div className="text-[10px] font-normal text-slate-400 tracking-wide">Admin Console</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-sky-600 text-white shadow-sm" : "hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800 space-y-1">
          <div className="px-3 py-1 text-xs text-slate-400 truncate">Signed in as <span className="text-slate-200 font-medium">{user?.name}</span></div>
          <Button variant="ghost" onClick={() => setPwOpen(true)} data-testid="open-change-password"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800">
            <KeyRound className="h-4 w-4 mr-2" /> Change Password
          </Button>
          <Button variant="ghost" onClick={() => setResetOpen(true)} data-testid="open-reset-data"
            className="w-full justify-start text-red-300 hover:text-white hover:bg-red-600/80">
            <AlertTriangle className="h-4 w-4 mr-2" /> Reset All Data
          </Button>
          <Button variant="ghost" onClick={() => logout().then(() => navigate("/login"))} data-testid="admin-logout-button"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <div className="md:hidden no-print fixed top-0 inset-x-0 z-40 bg-slate-900 text-white">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2"><Snowflake className="h-5 w-5 text-sky-400" /><span className="font-chivo font-black">SFX Admin</span></div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setPwOpen(true)} className="text-white"><KeyRound className="h-5 w-5" /></Button>
            <Button variant="ghost" size="sm" onClick={() => logout()} className="text-white"><LogOut className="h-5 w-5" /></Button>
          </div>
        </div>
        <div className="flex overflow-x-auto px-2 pb-2 gap-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `px-3 py-1.5 rounded text-xs whitespace-nowrap ${isActive ? "bg-sky-600" : "bg-slate-800"}`}>
              {n.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 min-w-0 md:ml-64 pt-28 md:pt-0">
        <div className="p-4 md:p-8 max-w-7xl">
          <Outlet />
        </div>
      </main>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <ResetDataDialog open={resetOpen} onOpenChange={setResetOpen} onDone={() => navigate("/admin")} />
    </div>
  );
}
